import { useIsCurrentUserPermittedTo } from "PFCore/helpers/use_is_permitted_to";
import { PermissionRule } from "PFTypes";
import PropTypes from "prop-types";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { getExceptionRequests } from "./api";
import ExceptionsButton from "./exceptions_button";
import css from "./exceptions_tab.module.scss";
import { exceptionsArrayToException } from "./helpers";
import ApproveOrRejectModal from "./modals/approve_or_reject_modal";
import EditRequestModal from "./modals/edit_request_modal";
import PaginatedExceptionsList from "./paginated_exceptions_list";

const ExceptionResponseButtons = ({ role, reload }) => {
  const isPermittedTo = useIsCurrentUserPermittedTo();
  const { t } = useTranslation("auditPlanner");

  const shortlistItem = role.shortlists.find(({ state }) => ["filled", "booked"].includes(state));
  const profile = shortlistItem?.profile;

  const [showApproveRejectModal, setShowApproveRejectModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [exception, setException] = useState(null);
  const openEditModal = useCallback(() => {
    if (!profile) {
      return;
    }
    getExceptionRequests(role.id, [profile.id]).then(({ entries: exceptions }) => {
      setException(exceptionsArrayToException(exceptions));
      setShowEditModal((showEditModal) => !showEditModal);
    });
  }, []);
  const openApproveRejectModal = useCallback(() => {
    if (!profile) {
      return;
    }
    getExceptionRequests(role.id, [profile.id]).then(({ entries: exceptions }) => {
      setException(exceptionsArrayToException(exceptions));
      setShowApproveRejectModal((showApproveRejectModal) => !showApproveRejectModal);
    });
  }, []);

  const canApproveReject = isPermittedTo(PermissionRule.AuditApprover);

  return (
    <span className={css.expectionsButtons}>
      <ExceptionsButton onClick={openEditModal} icon="edit" disabled={!profile}>
        {t("edit")}
      </ExceptionsButton>
      {canApproveReject && (
        <ExceptionsButton onClick={openApproveRejectModal} disabled={!profile}>
          {t("approve")} / {t("reject")}
        </ExceptionsButton>
      )}
      {showEditModal && exception && (
        <EditRequestModal
          role={role}
          close={() => setShowEditModal(false)}
          exception={exception}
          shortlistItem={shortlistItem}
          onSuccess={reload}
        />
      )}
      {showApproveRejectModal && exception && (
        <ApproveOrRejectModal
          role={role}
          close={() => setShowApproveRejectModal(false)}
          exception={exception}
          shortlistItem={shortlistItem}
          onSuccess={reload}
        />
      )}
    </span>
  );
};

ExceptionResponseButtons.propTypes = {
  role: PropTypes.object.isRequired,
  reload: PropTypes.func.isRequired
};

/* eslint-disable react/display-name*/
const ExceptionsTab = ({ data, fetchData, tab, fetchAllEngagements, workflowAutomation, roleFilters }) => {
  const columns = [
    {
      header: "",
      cellStyle: { width: "1%", whiteSpace: "nowrap", paddingRight: 0 },
      Component: ExceptionResponseButtons
    }
  ];

  return (
    <PaginatedExceptionsList
      data={data}
      fetchData={fetchData}
      roleFilters={roleFilters}
      tab={tab.id}
      columns={columns}
      fetchAllEngagements={fetchAllEngagements}
      workflowAutomation={workflowAutomation}
    />
  );
};

ExceptionsTab.propTypes = {
  data: PropTypes.shape({
    entries: PropTypes.arrayOf(PropTypes.object),
    meta: PropTypes.object,
    loading: PropTypes.bool
  }),
  tab: PropTypes.object.isRequired,
  fetchData: PropTypes.func.isRequired,
  fetchAllEngagements: PropTypes.func.isRequired,
  workflowAutomation: PropTypes.shape({
    auto_filling: PropTypes.number.isRequired,
    auto_shortlisting: PropTypes.number.isRequired,
    pending: PropTypes.number.isRequired
  }).isRequired,
  roleFilters: PropTypes.object.isRequired
};

export default ExceptionsTab;
